import { useMemo } from 'react';
import { ApolloClient, InMemoryCache, type NormalizedCacheObject } from '@apollo/client';
import merge from 'deepmerge';
// @ts-ignore - apollo-upload-client uses ESM exports which TypeScript doesn't resolve correctly
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';

let apolloClient: ApolloClient | undefined;

/**
 * Creates a new Apollo Client instance with the specified API URI and optional initial cache state.
 *
 * @param apiUri - The GraphQL API endpoint URI
 * @param initialState - Optional initial cache state to restore (from SSR/SSG or previous session)
 * @returns A new Apollo Client instance configured for the given API endpoint
 */
export function createApolloClient(
  apiUri: string,
  initialState: NormalizedCacheObject | null | undefined = null,
): ApolloClient {
  const isBrowser = typeof window !== 'undefined';

  const muteApolloDeprecations =
    process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PHASE === 'phase-production-build';

  if (muteApolloDeprecations) {
    (globalThis as Record<symbol, unknown>)[Symbol.for('apollo.deprecations')] = true;
  }

  const link = new UploadHttpLink({
    uri: apiUri,
    credentials: 'include',
  });

  return new ApolloClient({
    devtools: {
      enabled: isBrowser,
    },
    link,
    cache: new InMemoryCache().restore(initialState ?? {}),
  });
}

/**
 * Initializes or reuses an Apollo Client instance, merging server-side cache with client-side cache.
 *
 * This function implements the singleton pattern for Apollo Client:
 * - On the server (SSR/SSG): Always creates a new client per request
 * - On the client: Creates one client instance and reuses it across renders
 *
 * If initialState is provided, it merges with any existing client-side cache to prevent data loss.
 *
 * @param apiUri - The GraphQL API endpoint URI
 * @param initialState - Optional initial cache state from getStaticProps/getServerSideProps
 * @returns An Apollo Client instance (new on server, singleton on client)
 */
export function initializeApollo(
  apiUri: string,
  initialState: NormalizedCacheObject | null | undefined = null,
): ApolloClient {
  const _apolloClient = apolloClient ?? createApolloClient(apiUri);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  // Only merge if initialState has actual data (not empty object or null)
  if (initialState && Object.keys(initialState).length > 0) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const mergedData = merge(
      initialState as object,
      existingCache as object,
    ) as NormalizedCacheObject;

    // Restore the cache with the merged data
    _apolloClient.cache.restore(mergedData);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

/**
 * React hook that provides a memoized Apollo Client instance.
 *
 * The client is memoized based on apiUri and initialState to prevent unnecessary recreations.
 * Empty objects are normalized to null to prevent unnecessary recreations when initialState
 * is a new empty object reference on each render.
 *
 * @param apiUri - The GraphQL API endpoint URI
 * @param initialState - Optional initial cache state from getStaticProps/getServerSideProps
 * @returns A memoized Apollo Client instance
 */
export function useApollo(
  apiUri: string,
  initialState: NormalizedCacheObject | null | undefined = null,
): ApolloClient {
  // Normalize empty objects to null for stable dependency comparison
  // This prevents Apollo client recreation when initialState is a new {} on each render
  const normalizedInitialState =
    initialState && Object.keys(initialState).length > 0 ? initialState : null;

  const store = useMemo(
    () => initializeApollo(apiUri, normalizedInitialState),
    [apiUri, normalizedInitialState],
  );
  return store;
}
